<template>
    <BModal
        :model-value="modal"
        centered
        @update:modelValue="updateModal"
        no-close-on-backdrop
        no-close-on-escno-close-on-backdrop
        no-close-on-esc
    >
        <template #header>
            <div
                class="d-flex align-items-center justify-content-between modal-title w-100"
            >
                <h5 class="">Itinerary Item</h5>
                <BButton
                    variant="link"
                    class="close"
                    @click="updateModal(false)"
                >
                    <i class="ion ion-android-close"></i>
                </BButton>
            </div>
        </template>

        <BForm @submit.stop.prevent>
            <BRow>
                <BCol md="12">
                    <BFormGroup
                        label="Date *"
                        label-for="date"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            type="date"
                            v-model="form.date"
                            id="date"
                            :class="{
                                'is-invalid':
                                    v$.form.date.$dirty &&
                                    v$.form.date.$invalid,
                            }"
                        />
                        <BFormInvalidFeedback
                            :state="
                                v$.form.date.$dirty &&
                                v$.form.date.$invalid
                            "
                        >
                            {{ v$.form.date.$errors[0]?.$message }}
                        </BFormInvalidFeedback>
                    </BFormGroup>
                </BCol>
            </BRow>
            <BRow>
                <BCol md="12">
                    <BFormGroup
                        label="Route *"
                        class="text-start mb-3"
                        :class="{
                            'is-invalid':
                                v$.selectedRoute.$dirty &&
                                v$.selectedRoute.$invalid,
                        }"
                    >
                        <v-select
                            label="label"
                            :options="routeOptions"
                            v-model="selectedRoute"
                            :taggable="true"
                            placeholder="Select or type new route"
                            @update:modelValue="handleRouteUpdate"
                            :class="{
                                'is-invalid':
                                    v$.selectedRoute.$dirty &&
                                    v$.selectedRoute.$invalid,
                            }"
                        >
                            <template #no-options>
                                <input
                                    class="vs__search"
                                    v-bind="attributes"
                                    v-on="events"
                                    placeholder="Select or type new route"
                                />
                            </template>
                        </v-select>
                         <div
                            class="invalid-feedback d-block"
                            v-if="v$.selectedRoute.$dirty && v$.selectedRoute.$invalid"
                        >
                            {{ v$.selectedRoute.$errors[0]?.$message }}
                        </div>
                    </BFormGroup>
                </BCol>
            </BRow>
            <BRow>
                <BCol>
                    <BFormGroup
                        label="Route Description"
                        label-for="route_description"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            v-model="form.route_description"
                            type="text"
                            id="route_description"
                            placeholder="Optional route description"
                        />
                    </BFormGroup>
                </BCol>
            </BRow>
            <BRow>
                 <BCol>
                    <BFormGroup
                        label="Distance (km)"
                        label-for="distance"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            v-model="form.distance"
                            type="number"
                            id="distance"
                            placeholder="0"
                        />
                    </BFormGroup>
                </BCol>
            </BRow>
            <BRow>
                <BCol>
                    <BFormGroup
                        label="Hotel"
                        label-for="hotel_name"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            v-model="form.hotel_name"
                            type="text"
                            id="hotel_name"
                            placeholder="Hotel Name"
                        />
                    </BFormGroup>
                </BCol>
            </BRow>
            <BRow>
                <BCol md="12">
                    <BFormGroup
                        label="Amount (Salary)"
                        label-for="amount"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            v-model="form.amount"
                            type="number"
                            id="amount"
                            placeholder="0"
                            :class="{
                                'is-invalid':
                                    v$.form.amount.$dirty &&
                                    v$.form.amount.$invalid,
                            }"
                        />
                        <BFormInvalidFeedback
                            :state="
                                v$.form.amount.$dirty && v$.form.amount.$invalid
                            "
                        >
                            {{ v$.form.amount.$errors[0]?.$message }}
                        </BFormInvalidFeedback>
                    </BFormGroup>
                </BCol>
            </BRow>
        </BForm>

        <template #footer>
            <div class="d-flex justify-content-end">
                <BButton variant="primary" class="me-2" @click="handleAction"
                    >Save</BButton
                >
                <BButton variant="danger" @click="updateModal(false)"
                    >Cancel</BButton
                >
            </div>
        </template>
    </BModal>
</template>

<script setup>
import { defineProps, defineEmits, reactive, watch, ref } from "vue";
import {
    BModal,
    BRow,
    BCol,
    BForm,
    BFormInput,
    BButton,
    BFormGroup,
    BFormInvalidFeedback,
} from "bootstrap-vue-next";
import vSelect from "vue-select";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required, minValue } from "@vuelidate/validators";
import { useToast } from "vue-toastification";

const toast = useToast();

const props = defineProps({
    modal: Boolean,
    selectedData: Object,
    routeOptions: {
        type: Array,
        default: () => [],
    },
});

const emit = defineEmits(["update:modal", "addOrUpdate"]);

const updateModal = (value) => {
    emit("update:modal", value);
};

const formData = {
    date: "",
    route_uuid: null,
    route_name: "",
    route_description: "",
    hotel_name: "",
    amount: "",
    distance: "",
};

const form = reactive({ ...formData });
const selectedRoute = ref(null);

const rules = {
    form: {
        date: {
            required: helpers.withMessage("Date is required", required),
        },
        amount: {
             minValue: helpers.withMessage("Amount cannot be negative", minValue(0))
        },
    },
    selectedRoute: {
        required: helpers.withMessage("Route is required", required),
    }
};

const v$ = useVuelidate(rules, { form, selectedRoute });

watch(
    () => props.selectedData,
    (newData) => {
        if (newData && Object.keys(newData).length > 0) {
            Object.assign(form, { ...newData });
             // Set selectedRoute
            if (form.route_uuid) {
                const found = props.routeOptions.find(r => r.uuid === form.route_uuid);
                 selectedRoute.value = found || { label: form.route_name, uuid: form.route_uuid };
            } else {
                 selectedRoute.value = form.route_name;
            }
        } else {
            Object.assign(form, { ...formData });
            selectedRoute.value = null;
        }
        v$.value.$reset();
    },
    { immediate: true }
);

const handleRouteUpdate = (val) => {
    if (val) {
        if (typeof val === 'string') {
            form.route_uuid = null;
            form.route_name = val;
            form.route_description = "";
            form.distance = "";
        } else {
            form.route_uuid = val.uuid;
            form.route_name = val.label;
            form.route_description = val.description ? val.description : "";
            form.distance = val.distance ? val.distance : "";
        }
    } else {
        form.route_uuid = null;
        form.route_name = '';
        form.route_description = "";
        form.distance = "";
    }
    selectedRoute.value = val;
};

const resetForm = () => {
     Object.assign(form, { ...formData });
     selectedRoute.value = null;
     v$.value.$reset();
}

const handleAction = () => {
    props.selectedData && props.selectedData.index !== undefined ? update() : create();
};

const create = async () => {
    v$.value.$touch();
    if (!v$.value.$invalid) {
         try {
            const payload = { ...form };
            // Ensure amount is float
            payload.amount = parseFloat(payload.amount) || 0;
            payload.distance = parseFloat(payload.distance) || 0;
            delete payload.index;
            
            emit("addOrUpdate", payload);
            toast.success("Itinerary item added successfully");
            updateModal(false);
            resetForm();
         } catch(e) {
             toast.error("Failed to add itinerary item");
         }
    }
};

const update = async () => {
     v$.value.$touch();
    if (!v$.value.$invalid) {
         try {
            const payload = { ...form };
             payload.amount = parseFloat(payload.amount) || 0;
             payload.distance = parseFloat(payload.distance) || 0;
            emit("addOrUpdate", {
                index: props.selectedData.index,
                ...payload
            });
            toast.success("Itinerary item updated successfully");
            updateModal(false);
            resetForm();
         } catch(e) {
             toast.error("Failed to update itinerary item");
         }
    }
};

</script>
